package com.rajen.angrejidictionary.presentation.viewmodel

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.rajen.angrejidictionary.analytics.Umami
import com.rajen.angrejidictionary.data.local.AdminAuthManager
import com.rajen.angrejidictionary.data.remote.AdminApi
import com.rajen.angrejidictionary.data.remote.WordApi
import com.rajen.angrejidictionary.data.remote.dto.AdminWordSummary
import com.rajen.angrejidictionary.data.remote.dto.CreateWordRequest
import com.rajen.angrejidictionary.data.remote.dto.ItemDto
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch
import kotlinx.serialization.json.Json

class AdminDashboardViewModel {

    private val scope = CoroutineScope(Dispatchers.Default)

    // Word list state
    var words by mutableStateOf<List<AdminWordSummary>>(emptyList())
        private set
    var totalWords by mutableStateOf(0L)
        private set
    var currentPage by mutableStateOf(1)
        private set
    var isLoadingWords by mutableStateOf(false)
        private set
    var wordsError by mutableStateOf<String?>(null)
        private set
    var wordsActionMessage by mutableStateOf<String?>(null)
        private set

    // Word search state
    var wordSearchQuery by mutableStateOf("")
        private set
    var isSearchingWords by mutableStateOf(false)
        private set
    // null = not searching (show the paginated list); non-null = show these results instead.
    var wordSearchResults by mutableStateOf<List<ItemDto>?>(null)
        private set
    var wordSearchError by mutableStateOf<String?>(null)
        private set

    // Create / edit word state
    var jsonInput by mutableStateOf("")
        private set
    var parsedRequest by mutableStateOf<CreateWordRequest?>(null)
        private set
    var jsonError by mutableStateOf<String?>(null)
        private set
    var isSubmitting by mutableStateOf(false)
        private set
    var submitSuccess by mutableStateOf<String?>(null)
        private set
    var submitError by mutableStateOf<String?>(null)
        private set
    var lastSubmittedWord by mutableStateOf<String?>(null)
        private set

    // Premium toggle state
    var isPremium by mutableStateOf(false)
        private set
    // Edit only: false when the current value couldn't be loaded -> is_premium is omitted on save
    // (the server then keeps the current value). Always true in create mode.
    var premiumKnown by mutableStateOf(true)
        private set

    // Edit-specific state
    var editingWordId by mutableStateOf<String?>(null)
        private set
    var isLoadingWordDetail by mutableStateOf(false)
        private set
    var wordDetailError by mutableStateOf<String?>(null)
        private set
    var wordUpdateCompleted by mutableStateOf(false)
        private set

    // Delete-specific state
    var pendingDeleteWordId by mutableStateOf<String?>(null)
        private set
    var deletingWordId by mutableStateOf<String?>(null)
        private set

    // Word of the Day state
    var pinnedWordOfDayId by mutableStateOf<String?>(null)
        private set
    var pinnedWordOfDayLabel by mutableStateOf<String?>(null)
        private set
    var isLoadingWordOfDay by mutableStateOf(false)
        private set
    var wordOfDayError by mutableStateOf<String?>(null)
        private set
    // Id currently being pinned/unpinned (drives per-row loading state); "clear" while clearing.
    var pinningWordId by mutableStateOf<String?>(null)
        private set

    // ── Approve-via-Add-Word linkage ────────────────────────────────
    // Set when a suggestion's word isn't in the dictionary yet, via
    // startAddWordForApproval(). AdminDashboardPage watches (lastSubmittedWord,
    // pendingApprovalSuggestionId) together and calls
    // suggestionsViewModel.updateStatus(id, "approved") once both are set —
    // i.e. once this word has actually been created.
    var pendingApprovalSuggestionId by mutableStateOf<String?>(null)
        private set
    var pendingApprovalWord by mutableStateOf<String?>(null)
        private set
    // One-shot event: set at the exact moment a word is created *while*
    // pendingApprovalSuggestionId was linked to it — never inferred from
    // lastSubmittedWord (which stays non-null indefinitely after ANY
    // successful create/update, unrelated to any pending approval, and
    // caused a real bug where a stale lastSubmittedWord auto-approved a
    // suggestion on a later, unrelated redirect). Consumed immediately
    // after AdminDashboardPage acts on it.
    var approvalReadySuggestionId by mutableStateOf<String?>(null)
        private set

    // Session state
    var forceLogout by mutableStateOf(false)
        private set

    private val json = Json {
        ignoreUnknownKeys = true
        isLenient = true
        prettyPrint = true
    }

    init {
        loadWords()
        loadWordOfDay()
    }

    // ── Premium toggle ────────────────────────────────────────────

    fun onPremiumChange(value: Boolean) {
        isPremium = value
        premiumKnown = true
    }

    private fun resetPremium() {
        isPremium = false
        premiumKnown = true
    }

    fun onJsonInputChange(value: String) {
        jsonInput = value
        jsonError = null
        submitSuccess = null
        submitError = null
        parsedRequest = null
        lastSubmittedWord = null
        // Create mode: a new paste must never inherit ON from the previous word.
        // Edit mode keeps the value loaded from the server.
        if (editingWordId == null) resetPremium()
    }

    fun parseJson() {
        if (jsonInput.isBlank()) {
            jsonError = "Please paste the JSON content first"
            Umami.trackAdminWordParse(false, "empty-input")
            return
        }

        try {
            val request = json.decodeFromString<CreateWordRequest>(jsonInput)

            // Validate required fields
            if (request.word.englishWord.isBlank()) {
                jsonError = "english_word is required"
                Umami.trackAdminWordParse(false, "missing-english-word")
                return
            }

            if (request.word.meaningGujarati.isEmpty()) {
                jsonError = "At least one meaning_gujarati entry is required"
                Umami.trackAdminWordParse(false, "missing-meaning")
                return
            }

            if (request.word.partOfSpeech.isBlank()) {
                jsonError = "part_of_speech is required"
                Umami.trackAdminWordParse(false, "missing-part-of-speech")
                return
            }

            if (request.word.pronunciationGujarati.isBlank()) {
                jsonError = "pronunciation_gujarati is required"
                Umami.trackAdminWordParse(false, "missing-pronunciation")
                return
            }

            if (request.wordForms.isEmpty()) {
                jsonError = "word_forms_inflections must have at least one entry"
                Umami.trackAdminWordParse(false, "missing-word-forms")
                return
            }

            parsedRequest = request
            jsonError = null
            Umami.trackAdminWordParse(true)
        } catch (e: Exception) {
            jsonError = "Invalid JSON format: ${e.message?.take(100) ?: "Unknown error"}"
            parsedRequest = null
            Umami.trackAdminWordParse(false, "invalid-json")
        }
    }

    suspend fun submitWord() {
        val parsed = parsedRequest ?: run {
            submitError = "Please parse the JSON first"
            return
        }

        val editingId = editingWordId

        // Create: always explicit (false unless toggled on).
        // Edit: send the toggle value, or omit the field if the current value is unknown.
        val request = parsed.copy(
            isPremium = if (editingId == null || premiumKnown) isPremium else null
        )

        isSubmitting = true
        submitSuccess = null
        submitError = null

        try {
            if (editingId != null) {
                val response = AdminApi.updateWord(editingId, request)
                if (response.success && response.data != null) {
                    submitSuccess = "✅ Word '${request.word.englishWord}' updated!"
                    lastSubmittedWord = request.word.englishWord
                    Umami.trackAdminWordSubmit(true, request.word.englishWord)
                    editingWordId = null
                    jsonInput = ""
                    parsedRequest = null
                    resetPremium()
                    wordUpdateCompleted = true
                    refreshWordList()
                    if (pinnedWordOfDayId == editingId) {
                        pinnedWordOfDayLabel = request.word.englishWord
                    }
                } else {
                    submitError = response.error?.message ?: "Failed to update word"
                    Umami.trackAdminWordSubmit(false, request.word.englishWord, submitError)
                }
            } else {
                val response = AdminApi.createWord(request)
                if (response.success && response.data != null) {
                    submitSuccess = "✅ Word '${request.word.englishWord}' saved! ID: ${response.data}"
                    lastSubmittedWord = request.word.englishWord
                    Umami.trackAdminWordSubmit(true, request.word.englishWord)
                    jsonInput = ""
                    parsedRequest = null
                    resetPremium()
                    loadWords()

                    // This create was specifically to unblock a suggestion's approval —
                    // fire the one-shot event now, tied to *this* creation, and clear
                    // the link immediately so it can't be misread as still pending.
                    val suggestionId = pendingApprovalSuggestionId
                    if (suggestionId != null) {
                        approvalReadySuggestionId = suggestionId
                        pendingApprovalSuggestionId = null
                        pendingApprovalWord = null
                    }
                } else {
                    submitError = response.error?.message ?: "Failed to create word"
                    Umami.trackAdminWordSubmit(false, request.word.englishWord, submitError)
                }
            }
        } catch (_: IllegalStateException) {
            Umami.trackAdminWordSubmit(false, request.word.englishWord, "not-authenticated")
            AdminAuthManager.logout()
            forceLogout = true
        } catch (e: Exception) {
            val msg = e.message ?: ""
            if (msg.contains("401") || msg.contains("Unauthorized")) {
                Umami.trackAdminWordSubmit(false, request.word.englishWord, "unauthorized")
                AdminAuthManager.logout()
                forceLogout = true
            } else {
                submitError = "Network error: $msg"
                Umami.trackAdminWordSubmit(false, request.word.englishWord, "network-error")
            }
        } finally {
            isSubmitting = false
        }
    }

    fun loadWords(page: Int = 1) {
        isLoadingWords = true
        wordsError = null
        pendingDeleteWordId = null
        Umami.trackAdminWordsPageChange(page)

        scope.launch {
            try {
                val response = AdminApi.getWords(page, 20)

                if (response.success && response.data != null) {
                    words = response.data.words
                    totalWords = response.data.total
                    currentPage = page
                } else {
                    wordsError = response.error?.message ?: "Failed to load words"
                }
            } catch (e: Exception) {
                val msg = e.message ?: ""
                if (msg.contains("401") || msg.contains("Unauthorized")) {
                    AdminAuthManager.logout()
                    forceLogout = true
                } else {
                    wordsError = "Network error: $msg"
                }
            } finally {
                isLoadingWords = false
            }
        }
    }

    // ── Search ────────────────────────────────────────────────────
    // Uses the public /items/search endpoint (no auth needed) since it
    // covers the full dictionary, not just the current admin page.

    fun onWordSearchQueryChange(value: String) {
        wordSearchQuery = value
        if (value.isBlank()) {
            wordSearchResults = null
            wordSearchError = null
        }
    }

    fun searchWords() {
        val term = wordSearchQuery.trim()
        if (term.isBlank()) {
            wordSearchResults = null
            wordSearchError = null
            return
        }

        isSearchingWords = true
        wordSearchError = null
        pendingDeleteWordId = null
        Umami.trackAdminWordSearch(term)

        scope.launch {
            try {
                val response = WordApi.searchWords(term)
                if (response.success && response.data != null) {
                    wordSearchResults = response.data
                } else {
                    wordSearchResults = emptyList()
                    wordSearchError = response.error?.message
                }
            } catch (e: Exception) {
                wordSearchResults = emptyList()
                wordSearchError = "Network error: ${e.message}"
            } finally {
                isSearchingWords = false
            }
        }
    }

    fun clearWordSearch() {
        wordSearchQuery = ""
        wordSearchResults = null
        wordSearchError = null
    }

    /** Refreshes whichever view is currently active — search results or the paginated list. */
    private fun refreshWordList() {
        if (wordSearchResults != null) {
            searchWords()
        } else {
            loadWords(currentPage)
        }
    }

    // ── Edit ──────────────────────────────────────────────────────

    fun startEdit(id: String) {
        // Editing an existing word is unrelated to any pending "add word to
        // approve a suggestion" flow — drop the link rather than leave it
        // dangling and auto-approve something unexpectedly later.
        pendingApprovalSuggestionId = null
        pendingApprovalWord = null

        editingWordId = id
        isLoadingWordDetail = true
        wordDetailError = null
        jsonInput = ""
        parsedRequest = null
        jsonError = null
        submitSuccess = null
        submitError = null
        // Unknown until the detail call returns. Never default to "false" on edit.
        isPremium = false
        premiumKnown = false
        Umami.trackAdminWordEditStart(id)

        scope.launch {
            try {
                val response = AdminApi.getWordDetail(id)
                if (response.success && response.data != null) {
                    val request = response.data.toCreateWordRequest()
                    jsonInput = json.encodeToString(request)
                    parsedRequest = request
                    isPremium = response.data.isPremium ?: false
                    premiumKnown = response.data.isPremium != null
                } else {
                    wordDetailError = response.error?.message ?: "Failed to load word for editing"
                }
            } catch (e: Exception) {
                val msg = e.message ?: ""
                if (msg.contains("401") || msg.contains("Unauthorized")) {
                    AdminAuthManager.logout()
                    forceLogout = true
                } else {
                    wordDetailError = "Network error: $msg"
                }
            } finally {
                isLoadingWordDetail = false
            }
        }
    }

    fun cancelEdit() {
        editingWordId = null
        isLoadingWordDetail = false
        wordDetailError = null
        jsonInput = ""
        parsedRequest = null
        jsonError = null
        submitSuccess = null
        submitError = null
        pendingApprovalSuggestionId = null
        pendingApprovalWord = null
        approvalReadySuggestionId = null
        resetPremium()
    }

    fun consumeWordUpdateCompleted() {
        wordUpdateCompleted = false
    }

    // ── Approve-via-Add-Word ─────────────────────────────────────
    // Called by AdminDashboardPage when SuggestionsViewModel reports a
    // suggested word that isn't in the dictionary yet.

    fun startAddWordForApproval(suggestionId: String, word: String) {
        cancelEdit()
        pendingApprovalSuggestionId = suggestionId
        pendingApprovalWord = word
    }

    /** Detaches the pending suggestion link without touching whatever the admin has already typed. */
    fun cancelPendingApproval() {
        pendingApprovalSuggestionId = null
        pendingApprovalWord = null
        approvalReadySuggestionId = null
    }

    /** Clears the one-shot "word just created for this suggestion" event after it's been acted on. */
    fun consumeApprovalReady() {
        approvalReadySuggestionId = null
    }

    // ── Delete ────────────────────────────────────────────────────

    fun requestDeleteWord(id: String) {
        pendingDeleteWordId = if (pendingDeleteWordId == id) null else id
    }

    fun cancelDeleteWord() {
        pendingDeleteWordId = null
    }

    fun deleteWord(id: String) {
        deletingWordId = id
        pendingDeleteWordId = null

        scope.launch {
            try {
                val response = AdminApi.deleteWord(id)
                if (response.success) {
                    wordsActionMessage = "🗑 Word deleted"
                    Umami.trackAdminWordDelete(true, id)
                    if (editingWordId == id) {
                        cancelEdit()
                    }
                    if (pinnedWordOfDayId == id) {
                        pinnedWordOfDayId = null
                        pinnedWordOfDayLabel = null
                    }
                    refreshWordList()
                } else {
                    wordsError = response.error?.message ?: "Failed to delete word"
                    Umami.trackAdminWordDelete(false, id, wordsError)
                }
            } catch (e: Exception) {
                val msg = e.message ?: ""
                if (msg.contains("401") || msg.contains("Unauthorized")) {
                    AdminAuthManager.logout()
                    forceLogout = true
                } else {
                    wordsError = "Network error: $msg"
                    Umami.trackAdminWordDelete(false, id, "network-error")
                }
            } finally {
                deletingWordId = null
            }
        }
    }

    fun clearWordsActionMessage() {
        wordsActionMessage = null
    }

    // ── Word of the Day ──────────────────────────────────────────────

    fun loadWordOfDay() {
        isLoadingWordOfDay = true
        wordOfDayError = null

        scope.launch {
            try {
                val response = AdminApi.getWordOfDay()
                if (response.success) {
                    val id = response.data
                    pinnedWordOfDayId = id
                    pinnedWordOfDayLabel = null

                    if (id != null) {
                        // Best-effort lookup of the word's spelling for display; the
                        // endpoint only returns the id, and the pinned word may not
                        // be on whichever admin words page is currently loaded.
                        try {
                            val detail = AdminApi.getWordDetail(id)
                            if (detail.success && detail.data != null) {
                                pinnedWordOfDayLabel = detail.data.word.englishWord
                            }
                        } catch (_: Exception) {
                            // Non-fatal — we'll just show the raw id.
                        }
                    }
                } else {
                    wordOfDayError = response.error?.message ?: "Failed to load Word of the Day"
                }
            } catch (e: Exception) {
                val msg = e.message ?: ""
                if (msg.contains("401") || msg.contains("Unauthorized")) {
                    AdminAuthManager.logout()
                    forceLogout = true
                } else {
                    wordOfDayError = "Network error: $msg"
                }
            } finally {
                isLoadingWordOfDay = false
            }
        }
    }

    fun pinWordOfDay(id: String, englishWord: String) {
        pinningWordId = id

        scope.launch {
            try {
                val response = AdminApi.setWordOfDay(id)
                if (response.success) {
                    pinnedWordOfDayId = id
                    pinnedWordOfDayLabel = englishWord
                    wordsActionMessage = "📌 '$englishWord' pinned as Word of the Day"
                    Umami.trackAdminWordOfDayPin(true, id)
                } else {
                    wordsError = response.error?.message ?: "Failed to pin Word of the Day"
                    Umami.trackAdminWordOfDayPin(false, id, wordsError)
                }
            } catch (e: Exception) {
                val msg = e.message ?: ""
                if (msg.contains("401") || msg.contains("Unauthorized")) {
                    AdminAuthManager.logout()
                    forceLogout = true
                } else {
                    wordsError = "Network error: $msg"
                    Umami.trackAdminWordOfDayPin(false, id, "network-error")
                }
            } finally {
                pinningWordId = null
            }
        }
    }

    fun clearWordOfDayPin() {
        pinningWordId = "clear"

        scope.launch {
            try {
                val response = AdminApi.setWordOfDay(null)
                if (response.success) {
                    pinnedWordOfDayId = null
                    pinnedWordOfDayLabel = null
                    wordsActionMessage = "✅ Word of the Day override cleared — back to default rotation"
                    Umami.trackAdminWordOfDayPin(true, null)
                } else {
                    wordsError = response.error?.message ?: "Failed to clear Word of the Day override"
                    Umami.trackAdminWordOfDayPin(false, null, wordsError)
                }
            } catch (e: Exception) {
                val msg = e.message ?: ""
                if (msg.contains("401") || msg.contains("Unauthorized")) {
                    AdminAuthManager.logout()
                    forceLogout = true
                } else {
                    wordsError = "Network error: $msg"
                    Umami.trackAdminWordOfDayPin(false, null, "network-error")
                }
            } finally {
                pinningWordId = null
            }
        }
    }

    fun logout() {
        Umami.trackAdminLogout()
        AdminAuthManager.logout()

        // Fire-and-forget server invalidation
        scope.launch {
            try {
                AdminApi.logout()
            } catch (e: Exception) {
                // Ignore - user is already logged out locally
            }
        }
    }

    // is_premium is deliberately NOT copied here: the textarea JSON stays premium-free,
    // and the toggle state is applied in submitWord().
    private fun ItemDto.toCreateWordRequest(): CreateWordRequest = CreateWordRequest(
        word = word,
        wordForms = wordForms,
        similarWords = similarWords,
        oppositeWords = oppositeWords,
        usage = usage,
        phrasalUsage = phrasalUsage,
        tenseExplanation = tenseExplanation
    )
}